import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createV2Portfolio,createSubPortfolioTwo, updateV2PortfolioInfo, getV2CostBasisPresets, createPortfolioTwo, updatePortfolioTwo, batchUpdatePortfolioRatios } from '../../composables/fetchAPI';
import { usePortfolio } from '../../context/PortfolioContext';
import { getPortfolioType } from '../../utils/portfolioType';

interface AddEditPortfolioV2ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data?: any) => void;
  initialData?: any;
  allPortfolios?: any[]; // ✅ เพิ่ม Prop รับพอร์ตทั้งหมดมาแสดง
}

const AddEditPortfolioV2Modal: React.FC<AddEditPortfolioV2ModalProps> = ({ isOpen, onClose, onSave, initialData, allPortfolios }) => {
  const isEditMode = !!initialData;
  const {displayMode} = usePortfolio(); // เพิ่มการดึง displayMode
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [expectedRatio, setExpectedRatio] = useState<number>(0);
  const [portfolioType, setPortfolioType] = useState('STOCK');
  const [createDefaultSubPort, setCreateDefaultSubPort] = useState(true);

  // --- States สำหรับแก้ไขสัดส่วนพอร์ตอื่นๆ ---
  const [showAllRatios, setShowAllRatios] = useState(false);
  const [otherRatios, setOtherRatios] = useState<Record<string, { name: string; ratio: number }>>({});

  // --- States สำหรับ Settings ---
  const [feeMode, setFeeMode] = useState('ZERO');
  const [taxMode, setTaxMode] = useState('ZERO');

  // States สำหรับ Cost Basis
  const [costBasisPresets, setCostBasisPresets] = useState<any[]>([]);
  const [selectedCostBasisId, setSelectedCostBasisId] = useState<string>('WEBULL');

  // States สำหรับโหมด "กำหนดเอง (Custom)"
  const [customMethod, setCustomMethod] = useState('FIFO');
  const [customCompression, setCustomCompression] = useState('NONE');

  // พอร์ต CRYPTO รองรับแค่ WAC/FIFO แบบไม่บีบ lot (ไม่มี preset ของโบรกเกอร์หุ้น)
  const [cryptoMethod, setCryptoMethod] = useState<'WAC' | 'FIFO'>('WAC');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingPresets, setIsFetchingPresets] = useState(false);


  // ดึง Cost Basis Presets จาก API
  useEffect(() => {
    const fetchPresets = async () => {
      setIsFetchingPresets(true);
      try {
        const response = await getV2CostBasisPresets();
        const presets = response?.data?.presets || response?.presets || [];

        if (presets.length > 0) {
          setCostBasisPresets(presets);
          const hasWebull = presets.some((p: any) => p.id === 'WEBULL');
          setSelectedCostBasisId(hasWebull ? 'WEBULL' : presets[0].id);
        }
      } catch (error) {
        console.warn("Using fallback cost basis presets");
        const fallbackPresets = [
          { id: 'DIME', label: 'Dime - Compressed FIFO', config: { preset: 'DIME', method: 'COMPRESSED_FIFO', compressionStrategy: 'DAILY' } },
          { id: 'WEBULL', label: 'Webull - Weighted Average', config: { preset: 'WEBULL', method: 'WAC', compressionStrategy: 'NONE' } },
          { id: 'M1', label: 'M1 - Tax Minimization', config: { preset: 'M1', method: 'TAX_MINIMIZATION', compressionStrategy: 'NONE' } }
        ];
        setCostBasisPresets(fallbackPresets);
        setSelectedCostBasisId('WEBULL');
      } finally {
        setIsFetchingPresets(false);
      }
    };

    if (!isEditMode && isOpen) {
      fetchPresets();
    }
  }, [isEditMode, isOpen]);

  // โหลดข้อมูลเก่า (โหมดแก้ไข) และดึงลิสต์พอร์ตทั้งหมด
  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setBaseCurrency(initialData.baseCurrency || 'USD');
      setExpectedRatio(initialData.expectedRatio || 0);

      if (initialData.settings) {
        setFeeMode(initialData.settings.feeConfig?.mode || 'ZERO');
        setTaxMode(initialData.settings.taxConfig?.mode || 'ZERO');
        if (initialData.settings.costBasis?.preset) {
          setSelectedCostBasisId(initialData.settings.costBasis.preset);
          if (initialData.settings.costBasis.preset === 'CUSTOM') {
            setCustomMethod(initialData.settings.costBasis.method || 'FIFO');
            setCustomCompression(initialData.settings.costBasis.compressionStrategy || 'NONE');
          }
        }
      }
    } else {
      setName('');
      setDescription('');
      setBaseCurrency('USD');
      setExpectedRatio(0);
      setPortfolioType('STOCK');
      setCreateDefaultSubPort(true);
      setFeeMode('ZERO');
      setTaxMode('ZERO');
      setCustomMethod('FIFO');
      setCustomCompression('NONE');
      setCryptoMethod('WAC');
    }
  }, [initialData, isOpen]);

  // ชนิดของพอร์ตนี้ (โหมดแก้ไขใช้ตามที่หลังบ้านเก็บ เพราะเปลี่ยนชนิดทีหลังไม่ได้)
  const currentType = isEditMode ? getPortfolioType(initialData) : (portfolioType === 'CRYPTO' ? 'CRYPTO' : 'STOCK');
  const isCrypto = currentType === 'CRYPTO';

  // เตรียมข้อมูลพอร์ตอื่นๆ ไว้รอโชว์ตอนกด "แก้ไขสัดส่วน"
  // งบสัดส่วนหุ้น 100% กับคริปโต 100% แยกกัน จึงเอาเฉพาะพอร์ตชนิดเดียวกัน
  useEffect(() => {
    if (!allPortfolios) return;
    const initialOtherRatios: Record<string, { name: string; ratio: number }> = {};
    allPortfolios.forEach(p => {
      const pid = p.portfolioId || p.portId || p.id;
      // ข้ามพอร์ตปัจจุบันที่กำลังแก้ไข/สร้างอยู่
      if (initialData && pid === (initialData.portfolioId || initialData.portId || initialData.id)) {
        return;
      }
      if (getPortfolioType(p) !== currentType) return;
      initialOtherRatios[pid] = { name: p.name, ratio: p.expectedRatio || 0 };
    });
    setOtherRatios(initialOtherRatios);
  }, [initialData, isOpen, allPortfolios, currentType]);

  // คำนวณ Ratio รวมทั้งหมดเพื่อดักจับว่าเกิน 100% ไหม
  const totalOtherRatios = Object.values(otherRatios).reduce((sum, item) => sum + (Number(item.ratio) || 0), 0);
  const totalRatio = Number(expectedRatio) + totalOtherRatios;
  const isOverAllocated = totalRatio > 100;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('กรุณากรอกชื่อพอร์ต');
      return;
    }

    if (totalRatio > 100) {
      toast.error('สัดส่วนเป้าหมายรวมเกิน 100% กรุณาปรับสัดส่วนใหม่');
      return;
    }

    setIsLoading(true);

    try {
      // 1. จัดเตรียมข้อมูลสัดส่วนที่จะต้องส่งไป Batch Update (ใช้ Endpoint ของ V1)
      // เรารวบรวมพอร์ตที่แก้ไขสัดส่วนจาก otherRatios + พอร์ตปัจจุบันที่กำลังแก้/สร้าง
      const ratiosToUpdate = Object.entries(otherRatios).map(([pid, item]) => ({
        portfolioId: pid,
        expectedRatio: Number(item.ratio)
      }));

      // เพิ่มพอร์ตปัจจุบันที่กำลังแก้ไข/สร้างเข้าไปในลิสต์
      const currentPortId = initialData ? (initialData.portfolioId || initialData.portId || initialData.id) : null;
      if (currentPortId) {
        ratiosToUpdate.push({ portfolioId: currentPortId, expectedRatio: Number(expectedRatio) });
      } else {
        // หากเป็นการสร้างใหม่ จะไม่มี portId ให้ส่งใน batch update แต่เราจะไปตั้งค่าตอนสร้างพอร์ต
      }

      // 2. เรียก API ตามโหมด (V1 หรือ V2)
      if (displayMode === 'V1') {
        // --- LOGIC V1 ---
        // A. อัปเดตสัดส่วนทั้งหมดผ่าน Endpoint Batch Update (PATCH /portfolios/expected-ratios) [cite: 32, 179]
        if (ratiosToUpdate.length > 0) {
           // ตรวจสอบว่าในไฟล์ fetchAPI.ts ของคุณมีฟังก์ชันนี้ หรือใช้ fetch โดยตรงตาม spec [cite: 32, 33]
           await batchUpdatePortfolioRatios(ratiosToUpdate); 
        }

        // B. อัปเดตข้อมูลรายละเอียดพอร์ต (ชื่อ/คำอธิบาย)
        const payload = { 
            name: name.trim(), 
            description: description.trim(), 
            expectedRatio: Number(expectedRatio) 
        };


        if (isEditMode) {
          const portId = initialData.portfolioId || initialData.portId || initialData.id;
          await updatePortfolioTwo(portId, payload);
          toast.success("อัปเดตพอร์ตสำเร็จ!");
        } else {
          const newPortfolio = await createPortfolioTwo(payload);
          if (createDefaultSubPort) {
            await createSubPortfolioTwo(newPortfolio.portId, { name: 'Default', expectedRatio: 0 });
          }
          toast.success("สร้างพอร์ตสำเร็จ!");
        }

      } else {
        // ✅ โหมด V2: Logic การอัปเดตสัดส่วนอื่นๆ
        if (showAllRatios) {
          const promises = Object.entries(otherRatios).map(([pid, item]) => {
            const originalPort = allPortfolios?.find(p => (p.portfolioId || p.portId || p.id) === pid);
            if (originalPort && originalPort.expectedRatio !== item.ratio) {
              return updateV2PortfolioInfo(pid, { expectedRatio: item.ratio });
            }
            return Promise.resolve();
          });
          await Promise.all(promises);
        }

        if (isEditMode) {
          const updateData = {
            name: name.trim(),
            description: description.trim(),
            expectedRatio: Number(expectedRatio)
          };
          const portIdToUpdate = initialData.portfolioId || initialData.portId || initialData.id;
          await updateV2PortfolioInfo(portIdToUpdate, updateData);
          toast.success('อัปเดตข้อมูลพอร์ตสำเร็จ');
        } else {
        let finalCostBasis: any = { preset: 'WEBULL', method: 'WAC', compressionStrategy: 'NONE' };
        if (isCrypto) {
          finalCostBasis = {
            preset: 'CUSTOM',
            method: cryptoMethod,
            compressionStrategy: 'NONE',
            trackLots: cryptoMethod === 'FIFO',
            allowLotCompression: false,
          };
        } else if (selectedCostBasisId === 'CUSTOM') {
          finalCostBasis = { preset: 'CUSTOM', method: customMethod, compressionStrategy: customCompression };
        } else {
          const selectedPreset = costBasisPresets.find(p => p.id === selectedCostBasisId);
          if (selectedPreset && selectedPreset.config) {
            finalCostBasis = { ...selectedPreset.config };
          }
        }

        const newPortfolioData = {
          name: name.trim(),
          description: description.trim() || "",
          portfolioType: currentType,
          baseCurrency: "USD", // CRYPTO ต้องเป็น USD เท่านั้น
          expectedRatio: Number(expectedRatio),
          cashExpectedRatio: 0,
          createDefaultSubPort: createDefaultSubPort,
          defaultSubPort: createDefaultSubPort ? { name: 'Default', expectedRatio: 0 } : undefined,
          settings: {
            cashStrictMode: true,
            allowNegativeCash: false,
            feeConfig: { mode: feeMode },
            taxConfig: { mode: taxMode },
            costBasis: finalCostBasis,
          }
        };

        await createV2Portfolio(newPortfolioData);
        toast.success('สร้างพอร์ตการลงทุนใหม่สำเร็จ');
      }
}
      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving portfolio:', error);
      if (error?.response?.data?.code === "PORTFOLIO_EXPECTED_RATIO_EXCEEDED") {
        toast.error('สัดส่วนเป้าหมายรวมเกิน 100%');
      } else if (String(error?.code || '').startsWith('CRYPTO_')) {
        toast.error(error.th_message || error.message || 'สร้างพอร์ตคริปโตไม่สำเร็จ');
      } else {
        toast.error(error.message || 'เกิดข้อผิดพลาดในการบันทึกพอร์ต');
      }
    } finally {
      setIsLoading(false);
    }
  };


const showCompression = (
  (selectedCostBasisId === 'CUSTOM' && customMethod === 'COMPOSED_FIFO') || 
  (selectedCostBasisId === 'DIME')
);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" ></div>

      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#18181b] border border-gray-700 rounded-2xl shadow-2xl animate-fadeIn custom-scrollbar">

        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700 bg-gray-800/30 sticky top-0 z-10">
          <h2 className="text-xl font-bold text-yellow-400">
            {isEditMode ? 'แก้ไขข้อมูลพอร์ต' : 'สร้างพอร์ตการลงทุนใหม่'}
          </h2>
          {/* <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button> */}
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">ชื่อพอร์ต (Name) <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="เช่น US Growth Portfolio"
                required
              />
            </div>

            {/* ✅ ย้ายส่วนจัดการ Target Ratio มาอยู่ใต้ชื่อพอร์ต */}
            <div>
              <div className="flex justify-between items-end mb-1">
                <label className="block text-sm font-medium text-gray-300">
                  สัดส่วนเป้าหมาย (%) <span className="text-red-500">*</span>
                </label>
                {allPortfolios && (
                  <button
                    type="button"
                    onClick={() => setShowAllRatios(!showAllRatios)}
                    className="text-xs font-bold text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    {showAllRatios ? '▲ ปิดการแก้ไขสัดส่วน' : '▼ แก้ไขสัดส่วนพอร์ตอื่น'}
                  </button>
                )}
              </div>


              <input
                type="number" min="0" max="100" step="0.1"
                value={expectedRatio === 0 ? '' : expectedRatio}
                onChange={(e) => setExpectedRatio(Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 font-bold"
                placeholder="0.0"
              />

              {/* ✅ แผงควบคุมสัดส่วนพอร์ตทั้งหมด (แสดงเมื่อกดปุ่ม) */}
              {showAllRatios && (
                <div className="mt-3 p-4 bg-gray-900/80 border border-gray-700 rounded-lg animate-fadeIn">
                  <div className="flex justify-between text-sm mb-2 text-gray-300">
                    <span className="font-bold">{isCrypto ? 'สัดส่วนรวมพอร์ตคริปโต' : 'สัดส่วนรวมพอร์ตหุ้น'}</span>
                    <span className={isOverAllocated ? 'text-red-500 font-bold' : 'text-green-400 font-bold'}>
                      {totalRatio.toFixed(1)}% / 100%
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 h-2 rounded-full mb-4">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${isOverAllocated ? 'bg-red-500' : 'bg-green-400'}`}
                      style={{ width: `${Math.min(totalRatio, 100)}%` }}
                    ></div>
                  </div>

                  <div className="space-y-3 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                    {Object.entries(otherRatios).map(([pid, item]) => (
                      <div key={pid} className="flex justify-between items-center bg-[#18181b] p-2 rounded-lg border border-gray-700/50">
                        <span className="text-xs text-gray-300 truncate max-w-[150px] font-bold">{item.name}</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number" min="0" max="100" step="0.1"
                            className="w-20 bg-gray-800 border border-gray-600 rounded text-white text-right px-2 py-1 text-sm focus:border-yellow-400 focus:outline-none font-bold"
                            value={item.ratio === 0 ? '' : item.ratio}
                            onChange={(e) => setOtherRatios({ ...otherRatios, [pid]: { ...item, ratio: Number(e.target.value) } })}
                          />
                          <span className="text-xs text-gray-500 font-bold">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>


          {!isEditMode && displayMode === 'V2' && (
            <>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">ประเภทพอร์ต</label>
                  <select
                    value={portfolioType}
                    onChange={(e) => setPortfolioType(e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 appearance-none"
                  >
                    <option value="STOCK">หุ้น (STOCK)</option>
                    <option value="CRYPTO">คริปโต (CRYPTO)</option>
                    <option value="FOREX" disabled className="text-gray-500">ฟอเร็กซ์ (FOREX) - เร็วๆ นี้</option>
                    <option value="GOLD" disabled className="text-gray-500">ทองคำ (GOLD) - เร็วๆ นี้</option>
                  </select>
                  {portfolioType === 'CRYPTO' && (
                    <p className="mt-2 text-[11px] leading-relaxed text-orange-300/90">
                      พอร์ตคริปโตใช้เงิน USD เท่านั้น มีงบสัดส่วน 100% แยกจากพอร์ตหุ้น และเปลี่ยนชนิดพอร์ตภายหลังไม่ได้
                    </p>
                  )}
                </div>
              </div>

              {/* ... (ส่วน Advanced Settings ข้างล่างคงเดิม ไม่เปลี่ยน) ... */}
              <div className="border border-gray-700 rounded-xl p-4 bg-gray-800/30">
                <h3 className="text-sm font-bold text-gray-200 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  การตั้งค่าขั้นสูง (Settings)
                </h3>

                <div className="space-y-4">
                  {isCrypto ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">การคำนวณต้นทุน (Cost Basis)</label>
                      <select
                        value={cryptoMethod}
                        onChange={(e) => setCryptoMethod(e.target.value as 'WAC' | 'FIFO')}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 appearance-none"
                      >
                        <option value="WAC">WAC (ต้นทุนถัวเฉลี่ย)</option>
                        <option value="FIFO">FIFO (เข้าก่อน ออกก่อน)</option>
                      </select>
                      <p className="mt-2 text-[11px] text-gray-400 leading-relaxed">
                        พอร์ตคริปโตรองรับแค่ WAC และ FIFO (ไม่บีบรวม lot)
                      </p>
                    </div>
                  ) : (
                  <>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">การคำนวณต้นทุน (Cost Basis Preset)</label>
                    <select
                      value={selectedCostBasisId}
                      onChange={(e) => setSelectedCostBasisId(e.target.value)}
                      disabled={isFetchingPresets}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 appearance-none disabled:opacity-50"
                    >
                      {isFetchingPresets ? (
                        <option>กำลังโหลดข้อมูล...</option>
                      ) : (
                        <>
                          {costBasisPresets.map((preset) => (
                            <option key={preset.id} value={preset.id}>{preset.label}</option>
                          ))}
                          <option value="CUSTOM">กำหนดเอง (Custom)</option>
                        </>
                      )}
                    </select>

                    <div className="mt-2 p-2.5 bg-gray-900/50 border border-gray-700/50 rounded-lg">
                      {selectedCostBasisId === 'WEBULL' && (
                        <p className="text-[11px] text-gray-400 leading-relaxed">
                          <span className="text-blue-400 font-bold">WAC (ต้นทุนถัวเฉลี่ย):</span> เอาราคาทุกไม้มาเฉลี่ยรวมกัน เวลาขายหุ้นออกไปบางส่วน <span className="text-yellow-500">"ราคาต้นทุนเฉลี่ยจะยังคงเท่าเดิมไม่เปลี่ยนแปลง"</span> เข้าใจง่าย เหมาะกับสาย DCA
                        </p>
                      )}
                      {selectedCostBasisId === 'DIME' && (
                        <p className="text-[11px] text-gray-400 leading-relaxed">
                          <span className="text-emerald-400 font-bold">FIFO (เข้าก่อน ออกก่อน):</span> ระบบจะจำแยกเป็นไม้ๆ เวลาขายจะเอาหุ้น "ไม้เก่าสุด" ไปขายก่อนเสมอ ทำให้เวลาขาย <span className="text-yellow-500">"ต้นทุนเฉลี่ยในพอร์ตจะเปลี่ยนไป"</span>
                        </p>
                      )}
                      {selectedCostBasisId === 'M1' && (
                        <p className="text-[11px] text-gray-400 mt-2 italic">
                          <span className="text-pink-400 font-bold">M1 (Tax Minimization):</span> ระบบจะเลือก Lot หุ้นให้โดยอัตโนมัติเพื่อจัดลำดับผลกระทบทางภาษีให้เหมาะสมที่สุด
                        </p>
                      )}
                     
                    </div>
                  </div>

                  {selectedCostBasisId === 'CUSTOM' && (
                    <div className="grid grid-cols-2 gap-3 mt-2 animate-fadeIn bg-gray-900/50 p-3 rounded-lg border border-gray-700/50">
                      <div>
                        <label className="block text-xs font-medium text-yellow-500/80 mb-1">ระเบียบวิธี (Method)</label>
                        <select
                          value={customMethod}
                          onChange={(e) => setCustomMethod(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 appearance-none"
                        >
                          <option value="FIFO">FIFO (First In, First Out)</option>
                          <option value="WAC">WAC (ถัวเฉลี่ย)</option>
                          <option value="COMPRESSED_FIFO">Compressed FIFO</option>
                          <option value="TAX_MINIMIZATION">Tax Minimization</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-yellow-500/80 mb-1">การบีบอัด (Compression)</label>
                        <select
                          value={customCompression}
                          onChange={(e) => setCustomCompression(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 appearance-none"
                        >
                          <option value="NONE">ไม่มี (None)</option>
                          <option value="DAILY">รายวัน (Daily)</option>
                          <option value="MONTHLY">รายเดือน (Monthly)</option>
                          <option value="YEARLY">รายปี (Yearly)</option>
                        </select>
                      </div>
                    </div>
                  )}
                  </>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {/* <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">โหมดค่าธรรมเนียม (Fee)</label>
                      <select
                        value={feeMode}
                        onChange={(e) => setFeeMode(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 appearance-none"
                      >
                        <option value="ZERO">ไม่มีค่าธรรมเนียม (ZERO)</option>
                        <option value="FIXED">คงที่ (FIXED)</option>
                        <option value="PERCENTAGE">เปอร์เซ็นต์ (PERCENTAGE)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">โหมดภาษี (Tax)</label>
                      <select
                        value={taxMode}
                        onChange={(e) => setTaxMode(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 appearance-none"
                      >
                        <option value="ZERO">ไม่มีภาษี (ZERO)</option>
                        <option value="PERCENTAGE">เปอร์เซ็นต์ (PERCENTAGE)</option>
                      </select>
                    </div> */}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-gray-800/50 border border-gray-700 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createDefaultSubPort}
                    onChange={(e) => setCreateDefaultSubPort(e.target.checked)}
                    className="w-4 h-4 accent-yellow-400 rounded bg-gray-900 border-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-200">สร้างพอร์ตย่อยเริ่มต้นอัตโนมัติ (Default)</span>
                </label>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-700 sticky bottom-0 bg-[#18181b] pb-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-5 py-2.5 rounded-lg font-bold text-gray-300 hover:bg-gray-700 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2.5 rounded-lg font-bold text-[#18181b] bg-yellow-400 hover:bg-yellow-500 shadow-lg transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? 'กำลังบันทึก...' : (isEditMode ? 'บันทึกการแก้ไข' : 'สร้างพอร์ต')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditPortfolioV2Modal;